/** Liga o stream de eventos às consultas do board (SPEC-FE-001 § 3).
 *
 * O aviso não traz o dado — só diz que a projeção mudou. Quem busca é o
 * TanStack Query, que já sabe filtrar, paginar e desduplicar; invalidar a
 * chave é tudo o que falta.
 *
 * **Invalida, e não escreve no cache.** Escrever exigiria remontar aqui a
 * linha do read model a partir de um aviso que, de propósito, não carrega
 * conteúdo — e um erro nessa remontagem apareceria como um card com o dado
 * errado, que é pior que um card desatualizado por um segundo.
 *
 * Devolve se o stream está de pé. É o que faz a releitura por tempo recuar
 * para rede de segurança em vez de continuar sendo o mecanismo.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ouvirEventos, type Mudanca } from "@/lib/sse";

import { CHAVE_DO_QUADRO } from "./useQuadro";

/** Quais projeções mexem no que esta tela mostra.
 *
 * A timeline não avisa ninguém hoje, mas a lista existe para o dia em que
 * outra projeção passar a avisar: sem ela, o board releria por causa de uma
 * mudança que não muda um pixel dele.
 */
export const PROJECOES_DO_BOARD = new Set(["board"]);

export function interessaAoBoard(mudanca: Mudanca): boolean {
  return PROJECOES_DO_BOARD.has(mudanca.projecao);
}

export function useTempoReal(ativo = true): boolean {
  const cliente = useQueryClient();
  const [conectado, setConectado] = useState(false);

  useEffect(() => {
    if (!ativo) return;

    const assinatura = ouvirEventos({
      aoMudar: (mudanca) => {
        if (!interessaAoBoard(mudanca)) return;
        void cliente.invalidateQueries({ queryKey: CHAVE_DO_QUADRO });
      },
      aoConectar: setConectado,
    });

    return () => {
      assinatura.fechar();
      // Sem isto, desmontar a tela deixaria `conectado` em `true` para a
      // próxima montagem, que voltaria a acreditar em tempo real antes de o
      // stream existir.
      setConectado(false);
    };
  }, [ativo, cliente]);

  return conectado;
}
